import { useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';

import { TotemImage } from 'components/TotemImage';
import { DetailLink } from 'components/DetailsView';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { TotemInput } from 'components/TotemInput';
import { Loader, LoaderModeType } from 'components/Loader';
import { ListView, FieldToDisplay } from 'components/ListView';

import {
    GetMicrostoreColumnQuery,
    useGetTrayCapacityByColumnQuery,
    useUpdateTrayLocationLevelMutation,
    TotadminPermission,
} from 'data/__generated__';

type MicrostoreColumn = GetMicrostoreColumnQuery['microstoreColumn'];

type TrayLocationRow = {
    _id: string;
    image: JSX.Element;
    product: JSX.Element;
    quantity: JSX.Element;
    actions: JSX.Element;
};

const FIELDS_TO_DISPLAY: FieldToDisplay<TrayLocationRow>[] = [
    { fieldName: 'image', label: '', maxWidth: '60px' },
    { fieldName: 'product', label: 'Produit' },
    { fieldName: 'quantity', label: 'Remplissage', maxWidth: '180px' },
    { fieldName: 'actions', label: '', maxWidth: '150px' },
];

export const TrayLocationsContent = ({ microstoreColumn }: { microstoreColumn: MicrostoreColumn }) => {
    const [editingLocationId, setEditingLocationId] = useState<string | null>(null);
    const [editCurrentLevel, setEditCurrentLevel] = useState<string>('');

    const { data, loading } = useGetTrayCapacityByColumnQuery({
        variables: { microstoreColumnId: microstoreColumn._id },
    });

    const [updateLocation] = useUpdateTrayLocationLevelMutation({
        onCompleted: ({ updateTrayLocationLevel: { success, error } }) => {
            if (success) {
                toast.success('Quantité mis à jour');
                setEditingLocationId(null);
            } else {
                toast.error(`Erreur : ${error}`);
            }
        },
    });

    if (loading) {
        return <Loader mode={LoaderModeType.Spin} />;
    }

    const trayCapacity = data?.trayCapacityByMicrostoreColumnId;

    if (!trayCapacity || trayCapacity.locations.length === 0) {
        return (
            <EmptyContainer>
                <EmptyText>Aucun emplacement bac configuré pour cette colonne.</EmptyText>
            </EmptyContainer>
        );
    }

    function startEditing(locationId: string, currentLevel: number) {
        setEditingLocationId(locationId);
        setEditCurrentLevel(String(currentLevel));
    }

    function saveEditing(locationId: string, trayMaxCapacity: number) {
        const newLevel = Math.min(parseInt(editCurrentLevel) || 0, trayMaxCapacity);

        updateLocation({
            variables: {
                trayCapacityId: trayCapacity!._id,
                microstoreLocationId: locationId,
                currentLevel: newLevel,
            },
        });
    }

    const items: TrayLocationRow[] = trayCapacity.locations.map((location) => {
        const isEditing = editingLocationId === location.microstoreLocationId;
        const trayMaxCapacity = location.microstoreLocation.microstoreLocationTemplate.trayMaxCapacity;

        return {
            _id: location.microstoreLocationId,
            image: location.product?.imageUrl ? (
                <TotemImage
                    src={location.product.imageUrl}
                    alt={location.product.fullname || location.product.name}
                    size={40}
                />
            ) : (
                <></>
            ),
            product: location.product ? (
                <DetailLink
                    name={location.product.fullname || location.product.name}
                    value={location.product._id}
                    path="product"
                />
            ) : (
                <EmptyValue>-</EmptyValue>
            ),
            quantity: isEditing ? (
                <StockInputRow>
                    <TotemInput
                        type="number"
                        step="1"
                        min="0"
                        max={trayMaxCapacity}
                        value={editCurrentLevel}
                        onChange={setEditCurrentLevel}
                        $centerText
                    />
                    <span>/ {trayMaxCapacity}</span>
                </StockInputRow>
            ) : (
                <StockValue $isLow={trayMaxCapacity > 0 && location.currentLevel / trayMaxCapacity < 0.2}>
                    {location.currentLevel} / {trayMaxCapacity} g
                </StockValue>
            ),
            actions: isEditing ? (
                <ActionButtonsRow>
                    <TotemPrimaryButton
                        type="button"
                        onClick={() => saveEditing(location.microstoreLocationId, trayMaxCapacity)}
                        requiredPermissions={[TotadminPermission.TrayCapacitiesManage]}
                    >
                        Valider
                    </TotemPrimaryButton>
                    <TotemPrimaryButton type="button" $isSecondaryStyle onClick={() => setEditingLocationId(null)}>
                        Annuler
                    </TotemPrimaryButton>
                </ActionButtonsRow>
            ) : (
                <TotemPrimaryButton
                    type="button"
                    $isSecondaryStyle
                    onClick={() => startEditing(location.microstoreLocationId, location.currentLevel)}
                    requiredPermissions={[TotadminPermission.TrayCapacitiesManage]}
                >
                    Modifier
                </TotemPrimaryButton>
            ),
        };
    });

    return (
        <ListView<TrayLocationRow>
            fieldsToDisplay={FIELDS_TO_DISPLAY}
            items={items}
            keyExtractor={(item) => item._id}
        />
    );
};

const EmptyContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    padding: 32px;
`;

const EmptyText = styled.p`
    color: ${({ theme }) => theme.textColor};
    opacity: 0.6;
`;

const EmptyValue = styled.span`
    color: ${({ theme }) => theme.textColor};
    opacity: 0.5;
`;

const StockInputRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const StockValue = styled.span<{ $isLow?: boolean }>`
    font-weight: 600;
    color: ${({ $isLow, theme }) => ($isLow ? '#d31500' : theme.textColor)};
`;

const ActionButtonsRow = styled.div`
    display: flex;
    gap: 8px;
`;
